import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Platform, Pressable, TouchableOpacity, View } from 'react-native';

import { useTranslation } from 'react-i18next';

import CloseIcon from 'assets/images/svgs/ico_close.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';
import { VideoItem } from 'services/data-manager/types';

import { isWeb } from 'utils/responsiveness';

import { HOVER_EPISODE, HOVER_SEASON } from 'screens/Detail/languageConst';
import { CLOSE_BUTTON } from 'screens/Settings/languagesConst';

import BaseTileImage from 'components/BaseTileImage';
import {
    AspectRatio,
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';
import { PLACEHOLDER_ICON_CENTER } from 'components/Ribbon/constants';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

const {
    ACCESSIBILITY: {
        PLAYER: { ACCESSIBILITY_BINGE_WATCHING_RN },
    },
    BINGE_WATCHING: {
        BINGE_WATCHING_NEXT_STARTS_IN,
        BINGE_WATCHING_NEXT_LABEL,
    },
} = LANGUAGE_KEYS;

type Props = {
    nextEpisode: VideoItem;
    countdown: number;
    screenReaderEnabled?: boolean;
    onClose: () => void;
    onPlayNext: () => void;
};

const BingeWatchingOverlay: React.FC<Props> = ({
    nextEpisode,
    countdown,
    screenReaderEnabled,
    onClose,
    onPlayNext,
}) => {
    const { t } = useTranslation();
    const bingeWatchingContainerRef = useRef<View>(null);

    const [isImageHovered, setIsImageHovered] = useState(false);

    const metadata = useMemo(() => {
        const { seasonNumber, episodeNumber, seriesTitle, title } = nextEpisode;

        const seasonEpisode =
            seasonNumber != null && episodeNumber != null
                ? `${t(HOVER_SEASON)}${seasonNumber} ${t(
                      HOVER_EPISODE,
                  )}${episodeNumber}`
                : '';

        // Sx Ey Episode Title - if both available
        if (seasonEpisode && title) {
            return `${seasonEpisode} ${title}`;
        }

        // Episode Title - if season/episode not available
        if (title) {
            return title;
        }

        // Sx Ey Series Title - if episode title not available
        if (seasonEpisode && seriesTitle) {
            return `${seasonEpisode} ${seriesTitle}`;
        }

        // Series Title - as fallback
        return seriesTitle || '';
    }, [nextEpisode, t]);

    useEffect(() => {
        if (screenReaderEnabled) {
            setTimeout(() => {
                setAccessibilityFocusTo(bingeWatchingContainerRef);
            }, 500);
        } else if (isWeb) {
            setAccessibilityFocusTo(bingeWatchingContainerRef);
        }
    }, [screenReaderEnabled]);

    return (
        <View style={styles.container}>
            <TouchableOpacity
                style={styles.closeButton}
                onPress={onClose}
                accessible
                accessibilityRole="button"
                accessibilityLabel={t(CLOSE_BUTTON)}
            >
                <CloseIcon width={24} height={24} />
            </TouchableOpacity>
            <View style={styles.content}>
                <View style={styles.nextEpisodeContainer}>
                    <View style={styles.metadataContainer}>
                        <Text
                            bold
                            color={ETextColor.neutral}
                            category={ETextCategory.title2}
                            style={styles.nextLabel}
                        >
                            {t(BINGE_WATCHING_NEXT_LABEL)}
                        </Text>
                        <Text
                            bold
                            category={ETextCategory.title2}
                            color={ETextColor.neutral}
                            style={styles.metadata}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                        >
                            {metadata}
                        </Text>
                        <Text
                            color={ETextColor.neutral}
                            category={ETextCategory.body1}
                        >
                            {t(BINGE_WATCHING_NEXT_STARTS_IN, {
                                seconds: countdown.toString(),
                                interpolation: {
                                    prefix: '%',
                                    suffix: '%',
                                    escapeValue: false,
                                },
                            })}
                        </Text>
                    </View>
                    <Pressable
                        ref={bingeWatchingContainerRef}
                        accessible
                        accessibilityLabel={t(ACCESSIBILITY_BINGE_WATCHING_RN)}
                        {...(isWeb && {
                            'aria-label': t(ACCESSIBILITY_BINGE_WATCHING_RN),
                        })}
                        accessibilityRole={Platform.select({
                            web: 'imagebutton',
                            default: 'button',
                        })}
                        tabIndex={0}
                        style={[
                            styles.imageContainer,
                            isImageHovered && styles.imageHovered,
                        ]}
                        onPress={onPlayNext}
                        onHoverIn={() => setIsImageHovered(true)}
                        onHoverOut={() => setIsImageHovered(false)}
                        android_ripple={{ borderless: true }}
                    >
                        <BaseTileImage
                            style={styles.image}
                            contentId={
                                nextEpisode.id || nextEpisode.contentId || ''
                            }
                            contentType={ImageContentType.EPISODE}
                            imageType={ImageType.EPISODE}
                            width={ImageSize.LANDSCAPE.width}
                            aspect={AspectRatio.LANDSCAPE}
                            resizeMode="cover"
                            placeHolder={PLACEHOLDER_ICON_CENTER}
                        />
                    </Pressable>
                </View>
            </View>
        </View>
    );
};

export default BingeWatchingOverlay;
